import { Buffer } from "buffer";
import { models } from "jkurwa";

import {
  createErrorResult,
  createErrorResultFromException,
  createSuccessResult,
  ERRORS,
  IKeyBox,
  SimpleResult,
} from "@prostocheck/shared";

import { gatewayRequest } from "../gateway";
import {
  ICertificateDetails,
  IFetchCertificatesParams,
} from "./certificates.models";
import {
  buildCmpPayload,
  parseCmpResponse,
  readCertificateDetails,
} from "./certificates.utils";

/**
 * Most containers hold private keys only; the matching certificates live at the
 * issuing ЦСК and are pulled over CMP. Reports how many of them paired with a
 * loaded key. Without this step there is nothing to sign with.
 */
export const fetchCertificates = async ({
  keyBox,
  cmpUrl,
  timeoutMs,
}: IFetchCertificatesParams): Promise<SimpleResult<number>> => {
  const { box } = keyBox;

  // ГОСТ 34.311 for a legacy key, Купина for one read out of a modern
  // container — the ЦСК indexes its certificates by whichever its keys use.
  const keyIds = box.keys
    .filter(key => key.priv)
    .map(key => key.priv!.pub().keyid(keyBox.keyidAlgo ?? box.algo));

  if (keyIds.length === 0) {
    return createErrorResult(ERRORS.key.noPrivateKey);
  }

  let response;

  try {
    response = await gatewayRequest({
      gatewayUrl: keyBox.cmpGatewayUrl,
      request: { url: cmpUrl, body: buildCmpPayload(keyIds), timeoutMs },
    });
  } catch (requestError) {
    return createErrorResultFromException(
      requestError,
      ERRORS.key.certificateAuthorityUnreachable,
    );
  }

  if (response.status !== 200) {
    return createErrorResult(
      ERRORS.key.certificateAuthorityUnreachable,
      `HTTP ${response.status}`,
    );
  }

  const certificates = parseCmpResponse(Buffer.from(response.body));

  if (!certificates) {
    return createErrorResult(ERRORS.key.certificatesNotFound, cmpUrl);
  }

  certificates.forEach(cert => box.add({ cert }));
  box._indexKeys();

  // Count the pairs `Box.add` actually linked: it indexes a key and a
  // certificate under the same hash of the public key, so a modern cert pairs
  // even though its `subjectKeyIdentifier` extension carries the Купина hash.
  const matched = box.keys.filter(
    key => Boolean(key.priv) && Boolean(key.cert),
  ).length;

  if (matched === 0) {
    return createErrorResult(ERRORS.key.certificatesNotFound, cmpUrl);
  }

  return createSuccessResult(matched);
};

/**
 * Raw DER of every certificate currently attached to the box, so they can be
 * persisted and re-attached later without a second CMP round trip.
 */
export const exportCertificates = ({ box }: IKeyBox): Buffer[] =>
  box.keys.filter(key => key.cert).map(key => key.cert!.as_asn1());

/**
 * Re-attaches certificates that were exported earlier — the counterpart to
 * `exportCertificates`, used to restore a key box from storage instead of a
 * fresh CMP lookup.
 */
export const importCertificates = (
  { box }: IKeyBox,
  certificates: Buffer[],
): void => {
  certificates.forEach(der =>
    box.add({ cert: models.Certificate.from_asn1(der) }),
  );
  box._indexKeys();
};

/** Every certificate currently paired with a key in the box. */
export const getCertificates = ({ box }: IKeyBox): ICertificateDetails[] =>
  box.keys
    .filter(key => key.cert)
    .map(key => readCertificateDetails({ cert: key.cert!, priv: key.priv }));

/** The certificate the fiscal server will see: valid, usable for signing. */
export const getSigningCertificate = (
  keyBox: IKeyBox,
): SimpleResult<ICertificateDetails> => {
  const certificates = getCertificates(keyBox);
  const signing = certificates.find(
    certificate => certificate.hasPrivateKey && certificate.isActive,
  );

  if (signing) {
    return createSuccessResult(signing);
  }

  // An expired certificate is a different problem from no certificate at all,
  // and the user can only fix one of them.
  if (certificates.some(certificate => certificate.hasPrivateKey)) {
    return createErrorResult(ERRORS.key.certificateExpired);
  }

  return createErrorResult(ERRORS.key.certificatesNotFound);
};
